import Component from '@bythepixel/component-loader';

const DEFAULTS = {
  activeClass: 'accordion-list__title--active',
  triggerSelector: '[comp-accordion-trigger]',
  contentSelector: '[comp-accordion-content]',
  defaultMaxHeight: '0',
};

export default class Accordion extends Component {
  static selector = '[comp-accordion]';
  static loaderPriority = 'in-view';

  constructor(containerEl, options = DEFAULTS) {
    super(containerEl, options);
    Object.assign(this.$options, DEFAULTS, options);
    this.triggers = this.$container.querySelectorAll(this.$options.triggerSelector);
    this.contents = this.$container.querySelectorAll(this.$options.contentSelector);
    this.events();
    this.resetContent();
  }

  resetContent() {
    this.collapseAllContent();
    if (this.$container.dataset.accordionExpand) {
      this.expandContent(this.$container.dataset.accordionExpand);
    }
  }

  events() {
    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.triggerClickEventHandler(trigger));
    });

    // Some accordions need to re-calculate the max-height of their active content when the window resizes.
    // To allow accordions already created to function normally,
    // we conditionally apply this event listener.
    // This should probably be reworked entirely to better support this feature.
    if (this.$container.dataset.accordionExpand && this.$container.dataset.accordionExpand !== '') {
      let screenWidth = window.innerWidth; // Get current screen width
      window.addEventListener('resize', () => {
        if (window.innerWidth !== screenWidth) { // Only resetContent if screen width has been changed
          requestAnimationFrame(() => this.resetContent());
          screenWidth = window.innerWidth; // change screenWidth variable to be the new width of the screen
        }
      });
    }
  }

  /**
   * Show a single Accordion content, and hide all others.
   * @param  {number} index The index of the content to show.
   */
  expandContent(index) {
    this.$container.dataset.accordionExpand = index;
    if (this.triggers[index]) this.expandAccordion(this.triggers[index]);
  }

  /**
   * Collapses all Accordion contents, and remove active classes from triggers
   * @return {void}
   */
  collapseAllContent() {
    this.triggers.forEach((trigger) => {
      if (this.$container.dataset.accordionExpand !== ''
        && this.$container.dataset.accordionExpand === trigger.getAttribute('comp-accordion-trigger')) return;
      this.collapseContent(trigger);
    });
  }

  collapseContent(triggerElement) {
    const trigger = triggerElement;
    trigger.classList.remove(this.$options.activeClass);
    trigger.nextElementSibling.style.maxHeight = `${this.$options.defaultMaxHeight}px`;
  }

  /**
   * Handle trigger click event
   * @param {HTML element} trigger The Accordion trigger element
   * @return {void}
   */
  triggerClickEventHandler(trigger) {
    const close = trigger.classList.contains(this.$options.activeClass);
    this.$container.dataset.accordionExpand = trigger.getAttribute('comp-accordion-trigger');
    this.collapseAllContent();
    if (!close) this.expandAccordion(trigger);
  }

  /**
   * Given a trigger element, expand an Accordion content and hide all others.
   * @param  {HTMLElement} trigger The Accordion trigger element
   * @return {void}
   */
  expandAccordion(trigger) {
    applyExpandStyleToContent(trigger);
    trigger.classList.add(this.$options.activeClass);
  }
}

/**
 * Apply the styles required to expand an Accordion trigger.
 * @param  {HTMLElement} trigger The Accordion trigger element
 * @return {void}
 */
function applyExpandStyleToContent(trigger) {
  const content = trigger.nextElementSibling;

  const vertGap = parseInt(window.getComputedStyle(content)['row-gap'], 10) || 0;

  // Count up the height of all WYSIWYG child elements (p, ul, etc.)
  const totalHeight = [...content.children].map(el => {
    const style = window.getComputedStyle(el);
    const vertMargin = parseInt(style['margin-top'], 10) + parseInt(style['margin-bottom'], 10);
    const vertPadding = parseInt(style['padding-top'], 10) + parseInt(style['padding-bottom'], 10);
    return el.offsetHeight + vertMargin + vertPadding + vertGap;
  }).reduce((a, b) => a + b);
  // total height of all children
  content.style.maxHeight = `${totalHeight}px`;
}
